import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { Link } from 'dibs-elements/exports/Link';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { PdpSharedStorytellingRelatedContent_storyTellingTab } from './__generated__/PdpSharedStorytellingRelatedContent_storyTellingTab.graphql';

import styles from './PdpSharedStorytellingRelatedContent.scss';

type ArticleType = NonNullable<
    NonNullable<
        NonNullable<
            PdpSharedStorytellingRelatedContent_storyTellingTab['supplementaryEditorial']
        >['articles']
    >[0]
>;

const WORDPRESS_THUMB = 95;
const WORDPRESS_MEDIUM = 276;
const WORDPRESS_FULL = 768;
const getHeaderId = (index: number): string => `wp-article-header-${index}`;

type Props = {
    storyTellingTab: PdpSharedStorytellingRelatedContent_storyTellingTab;
};

const PdpSharedStorytellingRelatedContentComponent: FC<Props> = ({ storyTellingTab }) => {
    const { supplementaryEditorial } = storyTellingTab;
    const hasEditorial = !!supplementaryEditorial?.totalResults;

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    {hasEditorial && (
                        <div className={styles.editorialContent}>
                            <Heading className={styles.readMoreHeader}>
                                <FormattedMessage
                                    defaultMessage="From The Magazine"
                                    id="pdp.resp.storytelling.readMoreHeader"
                                />
                            </Heading>
                            {supplementaryEditorial?.articles?.map(
                                (article: ArticleType, index: number): ReactNode => (
                                    <Link
                                        ariaLabel={`Read more about ${article.title}`}
                                        ariaDescribedBy={`${getHeaderId(index)}`}
                                        buyerLinkType="standardLinkNoEffect"
                                        underline="none"
                                        href={article.link}
                                        target="_blank"
                                        dataTn={`wp-article-link-${index}`}
                                        className={styles.article}
                                        onClick={() => {
                                            trackEvent({
                                                category: 'ecommerce',
                                                action: 'promotion click',
                                                label: `editorial article - pdp: ${article.source} ${article.title}`,
                                            });
                                        }}
                                    >
                                        <div className={styles.articleImageWrapper}>
                                            <img
                                                className={styles.articleImage}
                                                src={article.imageUrl.thumb}
                                                srcSet={`
                ${article.imageUrl.thumb} ${WORDPRESS_THUMB}w,
                ${article.imageUrl.medium} ${WORDPRESS_MEDIUM}w,
                ${article.imageUrl.full} ${WORDPRESS_FULL}w
            `}
                                                sizes="(max-width: 320px) 100vw, 25vw"
                                                alt={article.title}
                                            />
                                        </div>
                                        <Heading
                                            className={styles.articleTitle}
                                            id={getHeaderId(index)}
                                            dangerouslySetInnerHTML={{ __html: article.title }}
                                        />
                                    </Link>
                                )
                            )}
                        </div>
                    )}
                </>
            )}
        </HeadingLevel>
    );
};

export const PdpSharedStorytellingRelatedContent = createFragmentContainer(
    PdpSharedStorytellingRelatedContentComponent,
    {
        storyTellingTab: graphql`
            fragment PdpSharedStorytellingRelatedContent_storyTellingTab on StoryTellingTab {
                supplementaryEditorial {
                    totalResults
                    articles {
                        title
                        imageUrl {
                            thumb
                            medium
                            full
                        }
                        link
                        source
                    }
                }
            }
        `,
    }
);
